import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/DesignPreview.css';

/**
 * /design-preview — INTERNAL PREVIEW PAGE
 * --------------------------------------------------
 * Not linked in any nav. Visit directly to compare luxe-warm artifact
 * concepts (wax-seal portraits, engraved gold coins, soft-edge tiles,
 * curiosity tap-to-reveal) before applying them to live marketing pages.
 *
 * Per user 2026-05-20: skew warm + inviting (rosé-gold + cream, soft
 * highlights), NOT austere Roman/gothic.
 */

const DesignPreview = () => {
  const [revealedCard, setRevealedCard] = useState(null);

  const toggleCard = (id) => {
    setRevealedCard(revealedCard === id ? null : id);
  };

  return (
    <div className="design-preview-page">
      <div className="dp-container">
        <header className="dp-header">
          <p className="dp-eyebrow">Internal Preview · Not Linked in Nav</p>
          <h1 className="dp-title">Luxe Artifact Concepts</h1>
          <p className="dp-lede">
            Side-by-side previews of the wax-seal portraits, engraved gold-coin
            stats, soft-edge tiles, and curiosity-reveal cards. Skim, tell me
            which to ship live.
          </p>
          <Link to="/founders" className="dp-back-link">&larr; back to Founders</Link>
        </header>

        {/* ============================================================
            CONCEPT 1 — FOUNDER BIO FRAMES (round 2)
            Original circle cropped out the CADENCE C-logo behind Lakia.
            New variants use wider / oval / arched shapes so the brand
            mark in the background stays visible.
            ============================================================ */}
        <section className="dp-section">
          <h2 className="dp-section-title">1 · Founder Bio Frames</h2>
          <p className="dp-section-note">
            Round 2 — the circle crop was hiding the CADENCE logo behind
            Lakia. These four shapes preserve more of the photo background
            while keeping the same warm copper / rosé-gold border treatment.
          </p>

          <div className="dp-grid dp-grid--portraits">
            {/* Variant C — Soft vertical oval (tallest reveal) */}
            <figure className="dp-portrait dp-portrait--oval">
              <div className="dp-portrait-frame">
                <img
                  src="/lakia-headshot.png"
                  alt="Founder portrait — soft oval variant"
                  className="dp-portrait-photo"
                />
              </div>
              <figcaption className="dp-portrait-plate">
                <p className="dp-portrait-name">Lakia D. Kelly</p>
                <p className="dp-portrait-role">Co-Founder · CMO</p>
              </figcaption>
              <p className="dp-variant-label">Variant C · Soft Vertical Oval</p>
            </figure>

            {/* Variant D — Apothecary arched cameo (round top, flat bottom) */}
            <figure className="dp-portrait dp-portrait--arch">
              <div className="dp-portrait-frame">
                <img
                  src="/lakia-headshot.png"
                  alt="Founder portrait — arched apothecary cameo variant"
                  className="dp-portrait-photo"
                />
              </div>
              <figcaption className="dp-portrait-plate">
                <p className="dp-portrait-name">Lakia D. Kelly</p>
                <p className="dp-portrait-role">Co-Founder · CMO</p>
              </figcaption>
              <p className="dp-variant-label">Variant D · Apothecary Arch</p>
            </figure>

            {/* Variant E — Rounded portrait frame (rectangular w/ corners) */}
            <figure className="dp-portrait dp-portrait--rect">
              <div className="dp-portrait-frame">
                <img
                  src="/lakia-headshot.png"
                  alt="Founder portrait — rounded rectangle frame variant"
                  className="dp-portrait-photo"
                />
              </div>
              <figcaption className="dp-portrait-plate">
                <p className="dp-portrait-name">Lakia D. Kelly</p>
                <p className="dp-portrait-role">Co-Founder · CMO</p>
              </figcaption>
              <p className="dp-variant-label">Variant E · Rounded Portrait Frame</p>
            </figure>

            {/* Variant F — Wide horizontal cameo (capsule, shows full backdrop) */}
            <figure className="dp-portrait dp-portrait--capsule">
              <div className="dp-portrait-frame">
                <img
                  src="/lakia-headshot.png"
                  alt="Founder portrait — wide capsule variant"
                  className="dp-portrait-photo"
                />
              </div>
              <figcaption className="dp-portrait-plate">
                <p className="dp-portrait-name">Lakia D. Kelly</p>
                <p className="dp-portrait-role">Co-Founder · CMO</p>
              </figcaption>
              <p className="dp-variant-label">Variant F · Tall Capsule</p>
            </figure>
          </div>
        </section>

        {/* ============================================================
            CONCEPT 2 — STAT MEDALLIONS (round 2)
            Original bright-gold disc felt celebratory for sober stats
            (12M misdiagnoses, 7+ year delays). New variants tone the
            palette down + give each medallion real floating 3D height.
            ============================================================ */}
        <section className="dp-section">
          <h2 className="dp-section-title">2 · Stat Medallions</h2>
          <p className="dp-section-note">
            Round 2 — the bright gold coin felt too celebratory for grim
            stats. These four medallions trade the carnival-gold face for
            sober metals (burnished bronze, eggplant + gold rim, aged
            patina, pewter) and add real floating 3D shadow + bevel so
            they sit above the page, not on it.
          </p>

          {/* ── Variant A · Burnished Bronze ─────────────────────────── */}
          <p className="dp-variant-label dp-variant-label--row">
            Variant A · Burnished Bronze
          </p>
          <div className="dp-grid dp-grid--coins">
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--bronze">
              <span className="dp-coin-figure">12M</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Misdiagnoses<br />Each Year</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--bronze">
              <span className="dp-coin-figure">1 / 20</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Adults<br />Misdiagnosed</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--bronze">
              <span className="dp-coin-figure">7+</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Years Average<br />Delay</span>
            </div></div>
          </div>

          {/* ── Variant B · Eggplant face + gold rim (palette match) ─── */}
          <p className="dp-variant-label dp-variant-label--row">
            Variant B · Eggplant Face + Engraved Gold Rim
          </p>
          <div className="dp-grid dp-grid--coins">
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--eggplant">
              <span className="dp-coin-figure">12M</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Misdiagnoses<br />Each Year</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--eggplant">
              <span className="dp-coin-figure">1 / 20</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Adults<br />Misdiagnosed</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--eggplant">
              <span className="dp-coin-figure">7+</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Years Average<br />Delay</span>
            </div></div>
          </div>

          {/* ── Variant C · Aged copper patina ───────────────────────── */}
          <p className="dp-variant-label dp-variant-label--row">
            Variant C · Aged Copper Patina
          </p>
          <div className="dp-grid dp-grid--coins">
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--patina">
              <span className="dp-coin-figure">12M</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Misdiagnoses<br />Each Year</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--patina">
              <span className="dp-coin-figure">1 / 20</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Adults<br />Misdiagnosed</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--patina">
              <span className="dp-coin-figure">7+</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Years Average<br />Delay</span>
            </div></div>
          </div>

          {/* ── Variant D · Pewter / aged silver ──────────────────────── */}
          <p className="dp-variant-label dp-variant-label--row">
            Variant D · Pewter Memorial
          </p>
          <div className="dp-grid dp-grid--coins">
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--pewter">
              <span className="dp-coin-figure">12M</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Misdiagnoses<br />Each Year</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--pewter">
              <span className="dp-coin-figure">1 / 20</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Adults<br />Misdiagnosed</span>
            </div></div>
            <div className="dp-coin"><div className="dp-coin-disc dp-coin-disc--pewter">
              <span className="dp-coin-figure">7+</span>
              <span className="dp-coin-divider" aria-hidden="true" />
              <span className="dp-coin-label">Years Average<br />Delay</span>
            </div></div>
          </div>
        </section>

        {/* ============================================================
            CONCEPT 3 — SOFT EDGE / TRANSPARENT BORDER VARIANTS
            Same tile shown three ways. Pick your favorite.
            ============================================================ */}
        <section className="dp-section">
          <h2 className="dp-section-title">3 · Soft-Edge Border Treatments</h2>
          <p className="dp-section-note">
            Currently every tile has a hard solid gold border. Below: three
            softer alternatives that breathe with the velvet background.
          </p>

          <div className="dp-grid dp-grid--borders">
            <div className="dp-tile dp-tile--hard">
              <p className="dp-tile-body">Promise tile with the existing hard gold border.</p>
              <p className="dp-variant-label">Variant A · Current (Hard Border)</p>
            </div>

            <div className="dp-tile dp-tile--fade">
              <p className="dp-tile-body">Promise tile with a soft fade edge — gold dissolves into the velvet.</p>
              <p className="dp-variant-label">Variant B · Soft Fade</p>
            </div>

            <div className="dp-tile dp-tile--hairline">
              <p className="dp-tile-body">Promise tile with a single gold hairline + drop shadow.</p>
              <p className="dp-variant-label">Variant C · Hairline + Shadow</p>
            </div>
          </div>
        </section>

        {/* ============================================================
            CONCEPT 4 — CURIOSITY TAP-TO-REVEAL TILE
            "Tap to see what makes us different" — pattern Promise uses.
            ============================================================ */}
        <section className="dp-section">
          <h2 className="dp-section-title">4 · Curiosity Tap-to-Reveal</h2>
          <p className="dp-section-note">
            Replace paragraph-heavy info blocks with a hooked tile. Tap to see
            the answer reveal in warm gold script. Used today on the Promise
            puzzle — would unify the trio + mission pillars on sister pages.
          </p>

          <div className="dp-grid dp-grid--curiosity">
            <button
              type="button"
              className={`dp-curiosity-tile ${revealedCard === 'a' ? 'is-revealed' : ''}`}
              onClick={() => toggleCard('a')}
            >
              {revealedCard === 'a' ? (
                <div className="dp-curiosity-reveal">
                  <p className="dp-curiosity-headline">Veteran-Owned</p>
                  <p className="dp-curiosity-detail">
                    Founded by two military medical veterans with 36+ years of
                    combined service.
                  </p>
                </div>
              ) : (
                <div className="dp-curiosity-hook">
                  <p className="dp-curiosity-eyebrow">Tap to reveal</p>
                  <p className="dp-curiosity-prompt">What makes us different?</p>
                </div>
              )}
            </button>

            <button
              type="button"
              className={`dp-curiosity-tile ${revealedCard === 'b' ? 'is-revealed' : ''}`}
              onClick={() => toggleCard('b')}
            >
              {revealedCard === 'b' ? (
                <div className="dp-curiosity-reveal">
                  <p className="dp-curiosity-headline">Cash-Pay Only</p>
                  <p className="dp-curiosity-detail">
                    No insurance friction. We work for you, not the carrier.
                  </p>
                </div>
              ) : (
                <div className="dp-curiosity-hook">
                  <p className="dp-curiosity-eyebrow">Tap to reveal</p>
                  <p className="dp-curiosity-prompt">Why no insurance?</p>
                </div>
              )}
            </button>
          </div>
        </section>

        {/* ============================================================
            CONCEPT 5 — LUXE BEVEL & FLOAT (round 2 for tile edges)
            Round 1 ("Velvet Edge") read as flat-glowy AI slop. These
            three variants give the bio tiles + launching banner real
            sculptural height + floating drop shadows, not a flat purple
            outline.
            ============================================================ */}
        <section className="dp-section">
          <h2 className="dp-section-title">5 · Luxe Bevel &amp; Float (Tile Edge — Round 2)</h2>
          <p className="dp-section-note">
            Round 2 — replacing the flat Velvet Edge. Each variant gives the
            tile real sculptural bevel + a heavy floating drop shadow so the
            card feels carved + lifted above the curtain, not painted onto it.
          </p>

          <div className="dp-grid dp-grid--bevels">
            {/* Variant A — Embossed Velvet Cushion */}
            <div className="dp-bevel dp-bevel--cushion">
              <p className="dp-bevel-title">Embossed Velvet Cushion</p>
              <p className="dp-bevel-body">
                Top-edge cream highlight catches light · bottom inner shadow
                presses in · heavy contact shadow underneath. Reads like
                tufted velvet upholstery.
              </p>
              <p className="dp-variant-label">Variant A · Embossed Cushion</p>
            </div>

            {/* Variant B — Carved Plum Plaque */}
            <div className="dp-bevel dp-bevel--plaque">
              <p className="dp-bevel-title">Carved Plum Plaque</p>
              <p className="dp-bevel-body">
                Deep inset bevel (light from top-left, shadow bottom-right)
                — looks chiseled out of solid stone · hairline gold seam
                at the inner edge · floating ground shadow.
              </p>
              <p className="dp-variant-label">Variant B · Carved Plaque</p>
            </div>

            {/* Variant C — Floating Velvet Card */}
            <div className="dp-bevel dp-bevel--floating">
              <p className="dp-bevel-title">Floating Velvet Card</p>
              <p className="dp-bevel-body">
                Soft rounded edges · subtle outer purple breath · directional
                long shadow under-right corner like the card is held above
                the page by an invisible hand.
              </p>
              <p className="dp-variant-label">Variant C · Floating Card</p>
            </div>
          </div>
        </section>

        {/* ============================================================
            CONCEPT 6 — UNIFIED HERITAGE APOTHECARY (preview before commit)
            Proposed propagation of the sepia bezel + Carved Plum Plaque
            tile language to OTHER content surfaces:
              · Pricing consultation cards  (currently flat copper/brass)
              · Compass article thumbnails  (currently plain tiles)
            ============================================================ */}
        <section className="dp-section">
          <h2 className="dp-section-title">6 · Heritage Apothecary — Propagation Demo</h2>
          <p className="dp-section-note">
            Preview only. Shows how the warm sepia bezel + Carved Plum Plaque
            bevel would re-skin the Pricing consultation cards and Compass
            article thumbnails to lock in one unified luxe language across
            every content surface (not just bio cards).
          </p>

          <div className="dp-grid dp-grid--heritage">

            {/* PRICING CARD demo */}
            <div className="dp-heritage-frame">
              <div className="dp-heritage-card dp-heritage-card--pricing">
                <p className="dp-heritage-eyebrow">Consultation Package</p>
                <h3 className="dp-heritage-title">Diagnostic Deep Dive</h3>
                <div className="dp-heritage-price">
                  <span className="dp-heritage-price-amount">$2,450</span>
                  <span className="dp-heritage-price-type">one-time</span>
                </div>
                <p className="dp-heritage-duration">90-min strategy call · 2-week follow-through</p>
                <ul className="dp-heritage-list">
                  <li>Full records review &amp; symptom mapping</li>
                  <li>Written second-opinion report</li>
                  <li>Specialist referral coordination</li>
                </ul>
                <p className="dp-variant-label">Pricing Tile — proposed</p>
              </div>
            </div>

            {/* COMPASS ARTICLE demo */}
            <div className="dp-heritage-frame">
              <div className="dp-heritage-card dp-heritage-card--article">
                <div className="dp-heritage-thumb" aria-hidden="true">
                  <span className="dp-heritage-thumb-mark">C</span>
                </div>
                <p className="dp-heritage-eyebrow">Compass · Women&rsquo;s Health</p>
                <h3 className="dp-heritage-title">When &lsquo;Normal Labs&rsquo; Aren&rsquo;t Normal</h3>
                <p className="dp-heritage-excerpt">
                  Reference ranges flatten individual physiology. Here&rsquo;s
                  how to read between the lines &mdash; and when to push for
                  a second opinion.
                </p>
                <p className="dp-heritage-meta">7 min read · by Lakia D. Kelly, DNP</p>
                <p className="dp-variant-label">Compass Article Thumb — proposed</p>
              </div>
            </div>

          </div>
        </section>

        <footer className="dp-footer">
          <p>Reply with the variants you want me to ship live (e.g. &ldquo;1A, 2, 3B, 4&rdquo;) and I&rsquo;ll wire them into the real pages.</p>
        </footer>
      </div>
    </div>
  );
};

export default DesignPreview;
